import React, { useState } from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import SymbolIcon from '../../../../assets/icons/SymbolIcon'
import StatusLineIcon from '../../../../assets/icons/StatusLineSymbol'
import ScalesAndLinesIcon from '../../../../assets/icons/ScalesAndLinesIcon'
import AppearanceIcon from '../../../../assets/icons/AppearanceIcon'
import CanvasIcon from '../../../../assets/icons/CanvasIcon'
import MultiStrikesExpiryMenu from '../../../../pages/home/openInterestHeader/MultiStrikesExpiryMenu/MultiStrikesExpiryMenu'
import MultiStrikesMenu from '../../../../pages/home/openInterestHeader/MultiStrikesMenu/MultiStrikesMenu'
import GroupOutputs from './configPopup/GroupOutputs'
import MultiStrikeScalesAndLinesSettings from './MultiStrikeScalesAndLinesSettings'
import MultiStrikeStatusLineSettings from './MultiStrikeStatusLineSettings'
import MultiStrikeCanvasSettings from './MultiStrikeCanvasSettings'
import {
    chartSettingsGridRowSx,
    chartSettingsLabelSx,
    chartSettingsRowControlsSx,
    chartSettingsSectionTitleSx,
    chartSettingsTabPanelSx,
} from '../../../chartSettings/chartSettingsLayout'

type Props = {
    viewKey: string
}

const tabStyle = {
    padding: '6px 0px 6px 20px',
    alignItems: 'start',
    justifyContent: 'center',
    textTransform: 'none',
    color: (theme: any) => theme.palette.text.primary,
    borderRadius: '4px',
    '&.Mui-selected': {
        color: (theme: any) => theme.palette.text.primary,
        backgroundColor: (theme: any) => theme.palette.action.selected,
        '& .tab-label': {
            fontWeight: 600,
        },
    },
} as const

const MultiStrikeSettingsTabs = ({ viewKey }: Props) => {
    const [tab, setTab] = useState(0)

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 320 }}>
            <Tabs
                orientation="vertical"
                value={tab}
                onChange={(_, newValue) => setTab(newValue)}
                sx={{
                    borderRight: 1,
                    borderColor: 'divider',
                    minWidth: 200,
                    paddingTop: '6px',
                }}
            >
                <Tab
                    value={0}
                    sx={tabStyle}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <SymbolIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Inputs
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={1}
                    sx={tabStyle}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <StatusLineIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Status Line
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={2}
                    sx={tabStyle}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <AppearanceIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Style
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={3}
                    sx={tabStyle}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <ScalesAndLinesIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Scales and lines
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={4}
                    sx={tabStyle}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CanvasIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Canvas
                            </Typography>
                        </span>
                    }
                />
            </Tabs>

            <Box sx={{ flex: 1, padding: 2, minWidth: 0 }}>
                <Box sx={{ display: 'grid', width: '100%', '& > *': { gridArea: '1 / 1' } }}>
                    <Box sx={{ visibility: tab === 0 ? 'visible' : 'hidden', width: '100%', ...chartSettingsTabPanelSx }}>
                        <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                            SELECTION
                        </Typography>
                        <Box sx={{ ...chartSettingsGridRowSx, width: '100%' }}>
                            <Typography sx={chartSettingsLabelSx}>Expiry</Typography>
                            <Box sx={{ ...chartSettingsRowControlsSx, width: '100%' }}>
                                <MultiStrikesExpiryMenu viewKey={viewKey} fullWidth />
                            </Box>
                        </Box>
                        <Box sx={{ ...chartSettingsGridRowSx, width: '100%' }}>
                            <Typography sx={chartSettingsLabelSx}>Strikes</Typography>
                            <Box sx={{ ...chartSettingsRowControlsSx, width: '100%' }}>
                                <MultiStrikesMenu viewKey={viewKey} fullWidth />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ visibility: tab === 1 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}>
                        <MultiStrikeStatusLineSettings viewKey={viewKey} />
                    </Box>
                    <Box sx={{ visibility: tab === 2 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}>
                        <GroupOutputs chartId={viewKey} />
                    </Box>
                    <Box sx={{ visibility: tab === 3 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}>
                        <MultiStrikeScalesAndLinesSettings viewKey={viewKey} />
                    </Box>
                    <Box sx={{ visibility: tab === 4 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}>
                        <MultiStrikeCanvasSettings viewKey={viewKey} />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default MultiStrikeSettingsTabs
